import React from 'react';
import { X } from 'lucide-react';

const CancelOrderModal = ({ orders: ordersHook }) => {
  const {
    showCancelOrderModal, closeCancelOrderModal,
    cancelReason, setCancelReason, cancelCustomReason, setCancelCustomReason,
    refundReasons, loadingRefundReasons, handleCancelOrderSubmit,
  } = ordersHook;

  if (!showCancelOrderModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800">Cancel Order</h3>
            <button onClick={closeCancelOrderModal} className="text-gray-500 hover:text-gray-700">
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Reason <span className="text-gray-500">(optional)</span>
              </label>
              <select
                value={cancelReason}
                onChange={(e) => {
                  setCancelReason(e.target.value);
                  if (e.target.value !== 'OTHER') setCancelCustomReason('');
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent"
              >
                <option value="">Select a reason (optional)</option>
                {loadingRefundReasons ? (
                  <option disabled>Loading reasons...</option>
                ) : (
                  refundReasons.map((reason) => (
                    <option key={reason.value} value={reason.value}>{reason.label}</option>
                  ))
                )}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Select a reason to help categorize the cancellation. This will be used for refund processing.
              </p>
            </div>

            {cancelReason === 'OTHER' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancelCustomReason}
                  onChange={(e) => setCancelCustomReason(e.target.value)}
                  rows={3}
                  placeholder="Enter the cancellation reason..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] focus:border-transparent resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Please provide details about why this order is being cancelled.</p>
              </div>
            )}

            {cancelReason && cancelReason !== 'OTHER' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Selected:</strong> {refundReasons.find((r) => r.value === cancelReason)?.label || cancelReason}
                </p>
                <p className="text-xs text-blue-600 mt-1">You can add additional details in the notes field when processing the refund.</p>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> If this order has a completed payment, an automatic refund request will be created.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button onClick={closeCancelOrderModal} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              Cancel
            </button>
            <button
              onClick={handleCancelOrderSubmit}
              disabled={cancelReason === 'OTHER' && !cancelCustomReason.trim()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm Cancellation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelOrderModal;
